interface SeaSnagLogoProps {
  className?: string;
  /** Always render the full-color gradient version */
  color?: boolean;
}

/**
 * Sea Snag wave logo.
 *
 * Default: monochrome using `currentColor` (respects text color / dark mode).
 * Wrap in a `group` and add `group-hover:` text color or use `color` prop
 * to show the gradient version.
 *
 * Hover behavior: wrap the parent in `group` class — the logo will
 * automatically switch from mono to full-color gradient on hover.
 */
export function SeaSnagLogo({ className = 'size-4', color = false }: SeaSnagLogoProps) {
  if (color) {
    return <ColorLogo className={className} />;
  }

  return <MonoLogo className={className} />;
}

function MonoLogo({ className }: { className: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="70 80 370 300"
      className={className}
      aria-hidden="true"
    >
      {/* Gradient defs — hidden by default, revealed on group-hover */}
      <defs>
        <linearGradient id="ss-wave1" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#0ea5e9" />
          <stop offset="100%" stopColor="#0369a1" />
        </linearGradient>
        <linearGradient id="ss-wave2" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#38bdf8" />
          <stop offset="100%" stopColor="#0284c7" />
        </linearGradient>
        <linearGradient id="ss-wave3" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#7dd3fc" />
          <stop offset="100%" stopColor="#38bdf8" />
        </linearGradient>
        <linearGradient id="ss-dark" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#0c4a6e" />
          <stop offset="100%" stopColor="#082f49" />
        </linearGradient>
      </defs>

      {/* Mono paths — visible by default */}
      <g className="transition-opacity duration-200 group-hover:opacity-0">
        <path
          d="M 120 140 C 160 140, 190 110, 240 110 C 290 110, 310 145, 350 145 C 370 145, 385 135, 400 125 L 400 165 C 385 175, 370 185, 350 185 C 310 185, 290 150, 240 150 C 190 150, 160 180, 120 180 Z"
          fill="currentColor"
        />
        <path
          d="M 110 200 C 150 200, 180 170, 230 170 C 280 170, 310 205, 360 205 C 380 205, 395 195, 410 185 L 410 225 C 395 235, 380 245, 360 245 C 310 245, 280 210, 230 210 C 180 210, 150 240, 110 240 Z"
          fill="currentColor"
          opacity="0.7"
        />
        <path
          d="M 100 260 C 140 260, 170 230, 220 230 C 270 230, 300 265, 350 265 C 370 265, 390 255, 410 245 L 410 285 C 390 295, 370 305, 350 305 C 300 305, 270 270, 220 270 C 170 270, 140 300, 100 300 Z"
          fill="currentColor"
          opacity="0.45"
        />
        <path
          d="M 130 320 C 170 320, 195 295, 240 295 C 285 295, 305 325, 345 325 C 365 325, 380 318, 395 308 L 395 340 C 380 350, 365 358, 345 358 C 305 358, 285 330, 240 330 C 195 330, 170 355, 130 355 Z"
          fill="currentColor"
          opacity="0.25"
        />
      </g>

      {/* Color paths — hidden by default, revealed on group-hover */}
      <g className="opacity-0 transition-opacity duration-200 group-hover:opacity-100">
        <path
          d="M 120 140 C 160 140, 190 110, 240 110 C 290 110, 310 145, 350 145 C 370 145, 385 135, 400 125 L 400 165 C 385 175, 370 185, 350 185 C 310 185, 290 150, 240 150 C 190 150, 160 180, 120 180 Z"
          fill="url(#ss-dark)"
        />
        <path
          d="M 110 200 C 150 200, 180 170, 230 170 C 280 170, 310 205, 360 205 C 380 205, 395 195, 410 185 L 410 225 C 395 235, 380 245, 360 245 C 310 245, 280 210, 230 210 C 180 210, 150 240, 110 240 Z"
          fill="url(#ss-wave1)"
        />
        <path
          d="M 100 260 C 140 260, 170 230, 220 230 C 270 230, 300 265, 350 265 C 370 265, 390 255, 410 245 L 410 285 C 390 295, 370 305, 350 305 C 300 305, 270 270, 220 270 C 170 270, 140 300, 100 300 Z"
          fill="url(#ss-wave2)"
        />
        <path
          d="M 130 320 C 170 320, 195 295, 240 295 C 285 295, 305 325, 345 325 C 365 325, 380 318, 395 308 L 395 340 C 380 350, 365 358, 345 358 C 305 358, 285 330, 240 330 C 195 330, 170 355, 130 355 Z"
          fill="url(#ss-wave3)"
          opacity="0.85"
        />
      </g>
    </svg>
  );
}

function ColorLogo({ className }: { className: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="70 80 370 300"
      className={className}
      aria-hidden="true"
    >
      <defs>
        <linearGradient id="ss-c-wave1" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#0ea5e9" />
          <stop offset="100%" stopColor="#0369a1" />
        </linearGradient>
        <linearGradient id="ss-c-wave2" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#38bdf8" />
          <stop offset="100%" stopColor="#0284c7" />
        </linearGradient>
        <linearGradient id="ss-c-wave3" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#7dd3fc" />
          <stop offset="100%" stopColor="#38bdf8" />
        </linearGradient>
        <linearGradient id="ss-c-dark" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#0c4a6e" />
          <stop offset="100%" stopColor="#082f49" />
        </linearGradient>
      </defs>
      <path
        d="M 120 140 C 160 140, 190 110, 240 110 C 290 110, 310 145, 350 145 C 370 145, 385 135, 400 125 L 400 165 C 385 175, 370 185, 350 185 C 310 185, 290 150, 240 150 C 190 150, 160 180, 120 180 Z"
        fill="url(#ss-c-dark)"
      />
      <path
        d="M 110 200 C 150 200, 180 170, 230 170 C 280 170, 310 205, 360 205 C 380 205, 395 195, 410 185 L 410 225 C 395 235, 380 245, 360 245 C 310 245, 280 210, 230 210 C 180 210, 150 240, 110 240 Z"
        fill="url(#ss-c-wave1)"
      />
      <path
        d="M 100 260 C 140 260, 170 230, 220 230 C 270 230, 300 265, 350 265 C 370 265, 390 255, 410 245 L 410 285 C 390 295, 370 305, 350 305 C 300 305, 270 270, 220 270 C 170 270, 140 300, 100 300 Z"
        fill="url(#ss-c-wave2)"
      />
      <path
        d="M 130 320 C 170 320, 195 295, 240 295 C 285 295, 305 325, 345 325 C 365 325, 380 318, 395 308 L 395 340 C 380 350, 365 358, 345 358 C 305 358, 285 330, 240 330 C 195 330, 170 355, 130 355 Z"
        fill="url(#ss-c-wave3)"
        opacity="0.85"
      />
    </svg>
  );
}
